import { Chip, IconButton, Menu, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import useKothar from "context/useKothar";
import { toast } from "react-toastify";
import ReferrerChangeDialog from "../../Clients/ReferrerChangeDialog";
import { canChangeReferrer } from "../../Clients/referrerAccess";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { eventClick, RefreshData } from "components/helper";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReferredClientDrawer from "../ReferredClients/ReferredClientDrawer";
import ClientProfileLink, { clientProfilePath } from "../ClientProfileLink";
import { listPartnerReferrals } from "../referralLeadsApi";

const STATES = ["", "SENT_TO_ORG", "ACTIVATED", "REJECTED", "EXISTING"];

const STATE_TONES = { SENT_TO_ORG: "info", ACTIVATED: "success", REJECTED: "error" };

/**
 * The shape the referrer dialog and its permission rule read. A row that came in as a portal lead
 * was referred through the portal; the server re-checks this on every change.
 */
const asReferredClient = (row) => ({
  id: row.clientId,
  name: row.name,
  referralOrigin: row.referralOrigin ?? (row.leadId ? "PORTAL" : "DIRECT"),
});

const RowActions = ({ row, canViewClient, onChangeReferrer, onOpenClient }) => {
  const [anchor, setAnchor] = useState(null);
  const client = asReferredClient(row);
  const canChange = Boolean(row.clientId) && canChangeReferrer(client);
  if (!row.clientId || (!canChange && !canViewClient)) return null;
  const run = (action) => () => {
    setAnchor(null);
    action();
  };
  return (
    <>
      <Tooltip title="Actions">
        <IconButton
          size="small"
          aria-label={`Actions for ${row.name || "this client"}`}
          onClick={(event) => {
            event.stopPropagation();
            setAnchor(event.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {canChange ? (
          <MenuItem onClick={run(() => onChangeReferrer(client))}>
            <SwapHorizIcon fontSize="small" className="mr-2" /> Change referrer
          </MenuItem>
        ) : null}
        {canViewClient ? (
          <MenuItem onClick={run(onOpenClient)}>
            <PersonOutlineIcon fontSize="small" className="mr-2" /> Open client
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

export const STATE_LABELS = { EXISTING: "Existing" };

/**
 * Everything this partner referred, paginated: portal leads plus clients attributed to them
 * directly before the portal existed (state EXISTING). A lead row opens the activation drawer,
 * so the decision happens with the same reason field and officer handoff wherever it is opened
 * from. An existing client has nothing to decide, so its row opens the client profile.
 */
const PartnerClientsPanel = ({ partnerId, onChanged }) => {
  const queryClient = useQueryClient();
  const canActivate = usePermission(P.REFERRAL.LEAD_ACTIVATE);
  const canReject = usePermission(P.REFERRAL.LEAD_REJECT);
  const canAssignOfficer = usePermission(P.CLIENTS.ASSIGN_OFFICER);
  const canViewClient = usePermission(P.CLIENTS.READ);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [search, setSearch] = useState("");
  const [state, setState] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [changingClient, setChangingClient] = useState(null);
  // The same staff list the client editor offers as referrers.
  const [{ usersList }, { refetchUsers }] = useKothar();
  const orgId = JSON.parse(localStorage.getItem("userDetail") || "{}")?.orgId;
  // Staff referrers come from the shared users list; load it if this page is the first to need it.
  useEffect(() => {
    if (changingClient && !usersList?.length) refetchUsers?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changingClient]);
  const orgUsers = useMemo(
    () => (usersList || []).filter((user) => user?.organization?.id === orgId),
    [usersList, orgId]
  );

  const filters = useMemo(
    () => ({
      page: pagination.pageIndex,
      size: pagination.pageSize,
      search,
      state,
    }),
    [pagination.pageIndex, pagination.pageSize, search, state]
  );

  const { data, isLoading, isFetching, refetch } = useQuery(
    ["referral-partner-referrals", partnerId, filters],
    () => listPartnerReferrals(partnerId, filters),
    { keepPreviousData: true, refetchOnMount: true, enabled: Boolean(partnerId) }
  );

  const changeFilter = (apply, value) => {
    apply(value);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries(["referral-leads"]);
    queryClient.invalidateQueries(["referral-partner-referrals"]);
    queryClient.invalidateQueries(["referral-overview"]);
    queryClient.invalidateQueries(["referral-overview", "partner", partnerId]);
    onChanged?.();
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
        Cell: ({ row }) => (
          <ClientProfileLink clientId={row.original.clientId}>
            {row.original.name}
          </ClientProfileLink>
        ),
      },
      { accessorKey: "email", header: "Email", size: 180 },
      { accessorKey: "number", header: "Phone", size: 130 },
      {
        accessorKey: "state",
        header: "State",
        size: 120,
        Cell: ({ row }) => (
          <Chip
            label={STATE_LABELS[row.original.state] || row.original.state}
            color={STATE_TONES[row.original.state] || "default"}
            size="small"
          />
        ),
      },
      {
        accessorKey: "referredAt",
        header: "Referred",
        size: 130,
        Cell: ({ row }) =>
          row.original.referredAt ? new Date(row.original.referredAt).toLocaleDateString() : "—",
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 80,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <RowActions
            row={row.original}
            canViewClient={canViewClient}
            onChangeReferrer={setChangingClient}
            onOpenClient={() => navigate(clientProfilePath(row.original.clientId))}
          />
        ),
      },
    ],
    [canViewClient, navigate]
  );

  const table = useMaterialReactTable({
    columns,
    data: data?.content || [],
    rowCount: data?.totalItems || 0,
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    state: { pagination, isLoading, showProgressBars: isFetching },
    enableStickyHeader: true,
    muiTableContainerProps: { sx: { maxHeight: "60vh", minHeight: "40vh", overflowY: "auto" } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        if (eventClick(event)) return;
        const { leadId, clientId } = row.original;
        if (leadId) setDetailId(leadId);
        else if (canViewClient && clientId) navigate(clientProfilePath(clientId));
      },
      sx: { cursor: "pointer", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } },
    }),
  });

  return (
    <div className="bg-app-surface rounded shadow p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <Typography variant="h6">
          Referred clients ({data?.totalItems ?? 0}) <RefreshData refreshData={refetch} />
        </Typography>
        <div className="flex flex-wrap items-center gap-3">
          <TextField
            size="small"
            label="Search name or email"
            value={search}
            onChange={(e) => changeFilter(setSearch, e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            select
            label="State"
            value={state}
            onChange={(e) => changeFilter(setState, e.target.value)}
            sx={{ minWidth: 150 }}
          >
            {STATES.map((option) => (
              <MenuItem key={option || "all"} value={option}>
                {option || "All"}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>
      <MaterialReactTable table={table} />

      {changingClient ? (
        <ReferrerChangeDialog
          open
          client={changingClient}
          users={orgUsers}
          onClose={() => setChangingClient(null)}
          onChanged={(result) => {
            toast.success(
              `${changingClient.name || "Client"} is now referred by ${
                result?.newLabel || "no one"
              }`
            );
            setChangingClient(null);
            refresh();
          }}
        />
      ) : null}

      <ReferredClientDrawer
        leadId={detailId}
        canActivate={canActivate}
        canReject={canReject}
        canAssignOfficer={canAssignOfficer}
        onClose={() => setDetailId(null)}
        onChanged={refresh}
      />
    </div>
  );
};

export default PartnerClientsPanel;
